import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import type { FileStore } from '@core/files';
import { applyThemeMode, type ThemeMode } from '@core/designsystem';
import { readOneOf, readPreference, writePreference } from '@core/settings/preferences';
import { isSendingConfigured, metricLabel, sendStatsNow, telemetry } from '@core/telemetry';
import { heavyCaseLine, reclaimableLine, storageLine } from './formatter';

const THEME_KEY = 'theme';
const THEMES: readonly ThemeMode[] = ['system', 'light', 'dark'];
const NAME_KEY = 'profile.name';

/** Сколько самых тяжёлых дел показываем: юрист чистит место, начиная с них. */
const HEAVY_LIMIT = 5;

/** Дело в списке самых тяжёлых — «Паспорт» §9. */
export interface HeavyCase {
  id: string;
  title: string;
  /** «412 МБ · 87 документов» */
  meta: string;
}

export interface SettingsState {
  isLoading: boolean;
  name: string;
  theme: ThemeMode;
  version: string;
  /** Где легли данные: `opfs` или `indexeddb` — это видно и юристу, и нам по письму. */
  storageKind: string;
  /** «1,2 ГБ в 14 делах» */
  storageLine: string;
  heavyCases: HeavyCase[];
  /** Сколько освободит уборка; `null` — убирать нечего. */
  reclaimable: string | null;
  /** Отправляем ли обезличенные цифры. Считаем их в любом случае, см. docs/TELEMETRY.md. */
  isStatsSendingEnabled: boolean;
  /** Настроен ли приёмник в этой сборке. Нет — обещать отправку нечестно. */
  isStatsConfigured: boolean;
  /** «Запусков: 4», «Открытий дел: 12» — то, что посчитано за сегодня. */
  todayStats: string[];
  /** Идёт ручная отправка: кнопка не должна выглядеть безответной. */
  isSendingStats: boolean;
  isCleaning: boolean;
  message: string | null;
}

/**
 * Данные настроек — «Структура» §14 и «Паспорт» §9.
 *
 * Хранилище здесь не украшение: это единственное место, где юрист видит, сколько заняли
 * материалы и что можно убрать. На телефоне место кончается раньше, чем терпение.
 */
export function useSettings(db: Repositories, files: FileStore, version: string) {
  const [name, setNameValue] = useState(() => readPreference(NAME_KEY) ?? '');
  const [theme, setThemeValue] = useState<ThemeMode>(() =>
    readOneOf(THEME_KEY, THEMES, 'system'),
  );

  const [storage, setStorage] = useState({ total: 0, cases: 0 });
  const [heavyCases, setHeavyCases] = useState<HeavyCase[]>([]);
  const [orphans, setOrphans] = useState<{ paths: string[]; bytes: number }>({
    paths: [],
    bytes: 0,
  });
  const [isStatsSendingEnabled, setStatsSending] = useState(() => telemetry.isSendingEnabled());
  const [todayStats, setTodayStats] = useState<string[]>(() => statLines());
  const [isSendingStats, setSendingStats] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [isCleaning, setCleaning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [version_, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const cases = await db.cases.list({});
      const sizes = await Promise.all(
        cases.map(async (item) => ({
          id: item.id,
          title: item.title,
          bytes: await files.caseSizeBytes(item.id),
          documents: (await db.documents.ofCase(item.id)).length,
        })),
      );

      const total = await files.totalSizeBytes();
      const live = await db.documents.liveFilesByCase();
      const paths = await files.orphans(live);
      const bytes = await files.sizeOf(paths);

      if (dropped) return;

      setStorage({ total, cases: sizes.filter((item) => item.bytes > 0).length });
      setHeavyCases(
        sizes
          .filter((item) => item.bytes > 0)
          .sort((a, b) => b.bytes - a.bytes)
          .slice(0, HEAVY_LIMIT)
          .map((item) => ({
            id: item.id,
            title: item.title,
            meta: heavyCaseLine(item.bytes, item.documents),
          })),
      );
      setOrphans({ paths, bytes });
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, files, version_]);

  const state: SettingsState = useMemo(
    () => ({
      isLoading,
      name,
      theme,
      version,
      storageKind: files.kind,
      storageLine: storageLine(storage.total, storage.cases),
      heavyCases,
      reclaimable: reclaimableLine(orphans.bytes),
      isStatsSendingEnabled,
      isStatsConfigured: isSendingConfigured(),
      todayStats,
      isSendingStats,
      isCleaning,
      message,
    }),
    [
      isLoading,
      name,
      theme,
      version,
      files.kind,
      storage,
      heavyCases,
      orphans,
      isStatsSendingEnabled,
      todayStats,
      isSendingStats,
      isCleaning,
      message,
    ],
  );

  const actions = useMemo(
    () => ({
      setName(value: string) {
        setNameValue(value);
        writePreference(NAME_KEY, value);
      },

      setTheme(value: ThemeMode) {
        setThemeValue(value);
        writePreference(THEME_KEY, value);
        applyThemeMode(value);
      },

      /**
       * Уборка осиротевших файлов.
       *
       * Это единственное место, где файлы удаляются насовсем: мягкое удаление документа
       * оставляет файл на диске, пока действует «Отменить» ([D-12](docs/DECISIONS.md)),
       * а потом он становится сиротой. Сколько освободится, юрист видит **до** нажатия.
       */
      async cleanUp() {
        if (orphans.paths.length === 0) return;

        setCleaning(true);
        const freed = await files.removePaths(orphans.paths);
        setCleaning(false);
        setMessage(freed > 0 ? `Освобождено ${Math.round(freed / 1024)} КБ` : 'Убирать было нечего');
        refresh();
      },

      /**
       * Выключатель отправки. Счёт при этом не прекращается: цифры остаются на
       * устройстве, и юрист видит их тут же — обещание «мы не видим ваших документов»
       * стоит ровно столько, сколько стоит возможность его проверить.
       */
      setStatsSending(enabled: boolean) {
        telemetry.setSendingEnabled(enabled);
        setStatsSending(enabled);
        setTodayStats(statLines());
      },

      /**
       * Отправить цифры немедленно, не дожидаясь ни часа, ни суток.
       *
       * Единственный способ проверить отправку на живом телефоне, где нет ни отладчика,
       * ни терпения ждать. Отвечает словами: дошло или нет — и это честный ответ,
       * а не «отправлено» в момент нажатия.
       */
      async sendStatsNow() {
        if (!telemetry.isSendingEnabled()) {
          setMessage('Отправка выключена — включите переключатель выше');
          return;
        }

        setSendingStats(true);
        const sent = await sendStatsNow(db, files, version).catch(() => false);
        setSendingStats(false);

        setTodayStats(statLines());
        setMessage(sent ? 'Отчёт отправлен' : 'Сервер не ответил — попробуем позже сами');
      },

      /**
       * Сброс номера установки — обещание из docs/TELEMETRY.md, которого до сих пор
       * не было ни в одной версии.
       *
       * После него присланное раньше связать с новым номером нельзя: цифры разъезжаются
       * на две «установки», и это правильная цена. Дела, документы и настройки
       * не трогаются вовсе — сбрасывается только случайный номер.
       */
      resetInstallId() {
        telemetry.resetInstallId();
        setMessage('Номер установки сброшен');
      },

      messageShown: () => setMessage(null),
      refresh,
    }),
    [db, files, version, orphans, refresh],
  );

  return { state, actions };
}

/** Что посчитано за сегодня, словами. Пустые счётчики не показываем: ноль не новость. */
function statLines(): string[] {
  return Object.entries(telemetry.todayValues())
    .filter(([, value]) => value > 0)
    .map(([metric, value]) => `${metricLabel(metric)}: ${value}`)
    .sort();
}

export type SettingsActions = ReturnType<typeof useSettings>['actions'];
